import { ChangeEvent, useMemo, useState } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { AXNUIContent, AXNUIContentMobileComplete } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { TextareaFormGroup } from "@containers/common/textarea-form-group/textarea-form-group";
import { ThemingConfigurationContainer } from "@containers/common/theming-configuration-container/theming-configuration-container";
import { ThemingFormContainer } from "@containers/common/theming-form-container/theming-form-container";
import { ThemingSubHeaderContainer } from "@containers/common/theming-sub-header-container/theming-sub-header-container";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { ThemingMobileCompletePageValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";

type ThemingMobileCompletePageTabProps = {
    isDisabled: boolean;
    validationErrors: ThemingMobileCompletePageValidationErrors;
    axnuiContent: AXNUIContent;
    inheritedAXNUIContent: AXNUIContent;
    pageLevel: PageLevelTypes;
    generatePreviewComponent: (currentTab: AXNUIThemingTabTypes) => React.ReactNode;
    updateAXNUIContentPropertyValue: (axnuiContent: AXNUIContent) => void;
};

export const ThemingMobileCompletePageTabContainer = (
    props: ThemingMobileCompletePageTabProps
) => {
    const {
        isDisabled,
        validationErrors,
        axnuiContent,
        inheritedAXNUIContent,
        pageLevel,
        generatePreviewComponent,
        updateAXNUIContentPropertyValue,
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const resource =
        resources.pages.verificationServiceDetails.tabs.theming.subTabs.mobileCompletePage;

    const updateAXNUIContentMobileCompleteFieldValue = (
        key: keyof AXNUIContentMobileComplete,
        value: string
    ) => {
        const newPropertyValue: AXNUIContent = {
            ...axnuiContent,
            mobileComplete: {
                ...axnuiContent.mobileComplete,
                [key]: value,
            },
        };
        updateAXNUIContentPropertyValue(newPropertyValue);
    };

    const handleMobileCompleteTitleChange = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const newValue = event.target.value;
        updateAXNUIContentMobileCompleteFieldValue(
            "title",
            newValue
        );
    };

    const handleSuccessMessageChange = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const newValue = event.target.value;
        updateAXNUIContentMobileCompleteFieldValue(
            "successMessage",
            newValue
        );
    };

    const handleErrorMessageChange = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const newValue = event.target.value;
        updateAXNUIContentMobileCompleteFieldValue(
            "errorMessage",
            newValue
        );
    };

    const handleResetMobileCompleteTitle = () => {
        updateAXNUIContentMobileCompleteFieldValue(
            "title",
            inheritedAXNUIContent.mobileComplete.title
        );
    };

    const handleResetSuccessMessage = () => {
        updateAXNUIContentMobileCompleteFieldValue(
            "successMessage",
            inheritedAXNUIContent.mobileComplete.successMessage
        );
    };

    const handleResetErrorMessage = () => {
        updateAXNUIContentMobileCompleteFieldValue(
            "errorMessage",
            inheritedAXNUIContent.mobileComplete.errorMessage
        );
    };

    const previewComponent = useMemo(() => {
        return generatePreviewComponent(AXNUIThemingTabTypes.MOBILE_COMPLETE_PAGE);
    }, [generatePreviewComponent]);

    return (
        <div>
            <ContentPageHeader
                title={resource.title}
            />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer
                    description={resource.description}
                />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <ThemingFormGroupWrapper
                            inheritedThemingPropertyValue={inheritedAXNUIContent.mobileComplete.title}
                            currentThemingPropertyValue={axnuiContent.mobileComplete.title}
                            pageLevel={pageLevel}
                        >
                            <InputFormGroup
                                controlId={"mobile-complete-title-text-input"}
                                automationId={"mobile-complete-title-text-input"}
                                label={resource.inputs.mobileCompleteTitle.label}
                                ariaLabel={resource.inputs.mobileCompleteTitle.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleMobileCompleteTitleChange}
                                value={axnuiContent.mobileComplete.title || ""}
                                disabled={isDisabled}
                                isRequired={true}
                                afterLabelTooltipText={resource.inputs.mobileCompleteTitle.tooltip}
                                afterLabelIcon={isDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetMobileCompleteTitle}
                            />
                        </ThemingFormGroupWrapper>
                        {validationErrors.titleError}

                        <ThemingFormGroupWrapper
                            inheritedThemingPropertyValue={inheritedAXNUIContent.mobileComplete.successMessage}
                            currentThemingPropertyValue={axnuiContent.mobileComplete.successMessage}
                            pageLevel={pageLevel}
                        >
                            <TextareaFormGroup
                                controlId={"mobile-complete-success-message-input"}
                                automationId={"mobile-complete-success-message-input"}
                                label={resource.inputs.successMessage.label}
                                ariaLabel={resource.inputs.successMessage.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleSuccessMessageChange}
                                value={axnuiContent.mobileComplete.successMessage || ""}
                                disabled={isDisabled}
                                isRequired={true}
                                afterLabelTooltipText={resource.inputs.successMessage.tooltip}
                                afterLabelIcon={isDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetSuccessMessage}
                            />
                        </ThemingFormGroupWrapper>
                        {validationErrors.successMessageError}

                        <ThemingFormGroupWrapper
                            inheritedThemingPropertyValue={inheritedAXNUIContent.mobileComplete.errorMessage}
                            currentThemingPropertyValue={axnuiContent.mobileComplete.errorMessage}
                            pageLevel={pageLevel}
                        >
                            <TextareaFormGroup
                                controlId={"mobile-complete-error-message-input"}
                                automationId={"mobile-complete-error-message-input"}
                                label={resource.inputs.errorMessage.label}
                                ariaLabel={resource.inputs.errorMessage.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleErrorMessageChange}
                                value={axnuiContent.mobileComplete.errorMessage || ""}
                                disabled={isDisabled}
                                isRequired={true}
                                afterLabelTooltipText={resource.inputs.errorMessage.tooltip}
                                afterLabelIcon={isDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetErrorMessage}
                            />
                        </ThemingFormGroupWrapper>
                        {validationErrors.errorMessageError}
                    </ThemingFormContainer>
                    {previewComponent}
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div>
    );
};
